import type * as Core from '../../types/calculation-core.js';
import type { QuadraticRoots, QuadraticValue } from '../../../source/shared/calculation-types.js';
import { failure, isFiniteNumber, resolveMetadata, success, validateMetadata, } from './types.js';
function staticMetadata(calculatorId: string, title: string, url: string, note: string): Core.CoreMetadata {
    return {
        calculatorId,
        version: '1.0.0',
        effectiveFrom: '2026-08-20',
        lastVerifiedAt: '2026-08-20',
        sources: [{
                id: `openstax-${calculatorId}`,
                title,
                publisher: 'OpenStax',
                url,
                verifiedAt: '2026-08-20',
            }],
        notes: [note],
    };
}
export const DEFAULT_RATIO_CONFIG = {
    metadata: staticMetadata('ratio-proportion', 'Prealgebra 2e — Solve Proportions and their Applications', 'https://openstax.org/books/prealgebra-2e/pages/6-5-solve-proportions-and-their-applications', 'Doğru ve ters orantı saf aritmetik yöntemlerdir; dönemsel veri içermez.'),
    maxAbsoluteInput: 1e12,
};
export const DEFAULT_PYTHAGOREAN_CONFIG = {
    metadata: staticMetadata('pythagorean', 'Prealgebra 2e — Use Properties of Angles, Triangles, and the Pythagorean Theorem', 'https://openstax.org/books/prealgebra-2e/pages/9-3-use-properties-of-angles-triangles-and-the-pythagorean-theorem', 'Araç yalnız Öklid düzlemindeki dik üçgenleri hesaplar.'),
    maxAbsoluteInput: 1e12,
};
export const DEFAULT_QUADRATIC_CONFIG = {
    metadata: staticMetadata('quadratic-equation', 'Algebra and Trigonometry 2e — Quadratic Equations', 'https://openstax.org/books/algebra-and-trigonometry-2e/pages/2-5-quadratic-equations', 'Katsayılar IEEE 754 sayı aralığında değerlendirilir; a sıfır olamaz.'),
    maxAbsoluteInput: 1e12,
};
export const DEFAULT_FRACTION_CONFIG = {
    metadata: staticMetadata('fraction', 'Prealgebra 2e — Fractions Key Concepts', 'https://openstax.org/books/prealgebra-2e/pages/4-key-concepts', 'Kesir işlemleri BigInt ile tam yapılır; ondalık gösterim yalnız yardımcı yaklaşık değerdir.'),
    maxAbsoluteInput: 1e9,
    maxAbsoluteInteger: 1e9,
};
function validateConfig(config: Core.BaseConfig): Core.InputError[] {
    const errors = [...validateMetadata(config.metadata)];
    if (!isFiniteNumber(config.maxAbsoluteInput) || config.maxAbsoluteInput <= 0) {
        errors.push({ field: 'config.maxAbsoluteInput', code: 'INVALID_CONFIG', message: 'Azami giriş sınırı pozitif ve sonlu olmalıdır.' });
    }
    return errors;
}
function numberErrors<T extends object, K extends keyof T & string>(input: T, fields: readonly K[], config: Core.BaseConfig): Core.InputError[] {
    const errors = [];
    for (const field of fields) {
        const value = input[field];
        if (typeof value !== 'number' || !isFiniteNumber(value)) {
            errors.push({ field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` });
        }
        else if (Math.abs(value) > config.maxAbsoluteInput) {
            errors.push({ field, code: 'OUT_OF_RANGE', message: `${field} izin verilen aralığın dışındadır.` });
        }
    }
    return errors;
}
function finiteResult<T>(value: T, numbers: readonly number[], metadata: Core.CoreMetadata) {
    return numbers.every(Number.isFinite)
        ? success(value, metadata)
        : failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Sonuç sonlu sayı aralığını aştı.' }], metadata);
}
function canonicalZero(value: number) {
    return Object.is(value, -0) ? 0 : value;
}
export function calculateRatio(input: Core.RatioInput, config: Core.BaseConfig = DEFAULT_RATIO_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_RATIO_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateConfig(config)
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'Orantı konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Orantı girdileri gereklidir.' }], metadata);
    }
    const errors = numberErrors(input, ['a', 'b', 'c'], config);
    if (input.mode !== 'direct' && input.mode !== 'inverse') {
        errors.push({ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'Orantı türü desteklenmiyor.' });
    }
    if (input.mode === 'direct' && input.a === 0) {
        errors.push({ field: 'a', code: 'DIVISION_BY_ZERO', message: 'Doğru orantıda A sıfır olamaz.' });
    }
    if (input.mode === 'inverse' && input.c === 0) {
        errors.push({ field: 'c', code: 'DIVISION_BY_ZERO', message: 'Ters orantıda C sıfır olamaz.' });
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const x = input.mode === 'direct' ? (input.b * input.c) / input.a : (input.a * input.b) / input.c;
    return finiteResult({ ...input, x }, [x], metadata);
}
export function calculatePythagorean(input: Core.PythagoreanInput, config: Core.BaseConfig = DEFAULT_PYTHAGOREAN_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_PYTHAGOREAN_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateConfig(config)
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'Pisagor konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Dik üçgen girdileri gereklidir.' }], metadata);
    }
    const errors = numberErrors(input, ['a', 'other'], config);
    if (input.mode !== 'hypotenuse' && input.mode !== 'leg') {
        errors.push({ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'Pisagor hesap türü desteklenmiyor.' });
    }
    for (const field of ['a', 'other'] as const) {
        if (isFiniteNumber(input[field]) && input[field] <= 0) {
            errors.push({ field, code: 'OUT_OF_RANGE', message: 'Kenar uzunluğu sıfırdan büyük olmalıdır.' });
        }
    }
    if (input.mode === 'leg' && input.other <= input.a) {
        errors.push({ field: 'other', code: 'OUT_OF_RANGE', message: 'Hipotenüs dik kenardan büyük olmalıdır.' });
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const a = input.a;
    const b = input.mode === 'hypotenuse'
        ? input.other
        : Math.sqrt(input.other - a) * Math.sqrt(input.other + a);
    const c = input.mode === 'hypotenuse' ? Math.hypot(a, input.other) : input.other;
    const result = input.mode === 'hypotenuse' ? c : b;
    const perimeter = a + b + c;
    const area = (a * b) / 2;
    return finiteResult({ mode: input.mode, a, b, c, unknown: input.mode === 'hypotenuse' ? 'c' : 'b', result, perimeter, area }, [a, b, c, result, perimeter, area], metadata);
}
export function calculateQuadratic(input: Core.QuadraticInput, config: Core.BaseConfig = DEFAULT_QUADRATIC_CONFIG): Core.Calculation<QuadraticValue> {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_QUADRATIC_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateConfig(config)
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'Denklem konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Denklem katsayıları gereklidir.' }], metadata);
    }
    const errors = numberErrors(input, ['a', 'b', 'c'], config);
    if (input.a === 0)
        errors.push({ field: 'a', code: 'OUT_OF_RANGE', message: 'İkinci dereceden denklemde A sıfır olamaz.' });
    if (errors.length > 0)
        return failure(errors, metadata);
    const bSquared = input.b * input.b;
    const fourAC = 4 * input.a * input.c;
    const rawDiscriminant = bSquared - fourAC;
    const discriminantScale = Math.abs(bSquared) + Math.abs(fourAC);
    const discriminantTolerance = Number.EPSILON * 8 * discriminantScale;
    const discriminant = Math.abs(rawDiscriminant) <= discriminantTolerance ? 0 : rawDiscriminant;
    const rootSum = -input.b / input.a;
    const rootProduct = input.c / input.a;
    const vertexX = -input.b / (2 * input.a);
    const vertexY = canonicalZero(-discriminant / (4 * input.a));
    let roots: QuadraticRoots;
    if (discriminant > 0) {
        let x1;
        let x2;
        const squareRoot = Math.sqrt(discriminant);
        const q = -0.5 * (input.b + (input.b >= 0 ? squareRoot : -squareRoot));
        const qRoot = canonicalZero(q / input.a);
        const reciprocalRoot = canonicalZero(input.c / q);
        if (input.b < 0) {
            x1 = qRoot;
            x2 = reciprocalRoot;
        }
        else {
            x1 = reciprocalRoot;
            x2 = qRoot;
        }
        roots = { rootType: 'two-real', x1, x2 };
    }
    else if (discriminant === 0) {
        const x1 = canonicalZero(-input.b / (2 * input.a));
        roots = { rootType: 'double-real', x1, x2: x1 };
    }
    else {
        const real = canonicalZero(-input.b / (2 * input.a));
        const imaginary = Math.sqrt(-discriminant) / Math.abs(2 * input.a);
        roots = { rootType: 'complex', x1: { real, imaginary }, x2: { real, imaginary: -imaginary } };
    }
    const rootValues = [roots.x1, roots.x2].flatMap((root) => typeof root === 'number' ? [root] : [root.real, root.imaginary]);
    return finiteResult({
        ...input,
        discriminant,
        ...roots,
        rootSum,
        rootProduct,
        vertex: { x: vertexX, y: vertexY },
    }, [discriminant, rootSum, rootProduct, vertexX, vertexY, ...rootValues], metadata);
}
function bigAbs(value: bigint) { return value < 0n ? -value : value; }
function bigGcd(a: bigint, b: bigint) {
    let left = bigAbs(a);
    let right = bigAbs(b);
    while (right !== 0n)
        [left, right] = [right, left % right];
    return left;
}
function simplifyFraction(numerator: bigint, denominator: bigint) {
    const sign = denominator < 0n ? -1n : 1n;
    const gcd = bigGcd(numerator, denominator);
    return [(numerator / gcd) * sign, (denominator / gcd) * sign];
}
export function calculateFraction(input: Core.FractionInput, config: Core.BaseConfig & {
    readonly maxAbsoluteInteger: number;
} = DEFAULT_FRACTION_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_FRACTION_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? [...validateConfig(config)]
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'Kesir konfigürasyonu geçersiz.' }];
    if (config && (!Number.isSafeInteger(config.maxAbsoluteInteger) || config.maxAbsoluteInteger <= 0)) {
        configErrors.push({ field: 'config.maxAbsoluteInteger', code: 'INVALID_CONFIG', message: 'Kesir tamsayı sınırı pozitif güvenli tamsayı olmalıdır.' });
    }
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'İki kesir gereklidir.' }], metadata);
    }
    const errors = [];
    for (const field of ['numerator1', 'denominator1', 'numerator2', 'denominator2'] as const) {
        const value = input[field];
        if (!Number.isSafeInteger(value)) {
            errors.push({ field, code: 'OUT_OF_RANGE', message: `${field} güvenli bir tamsayı olmalıdır.` });
        }
        else if (Math.abs(value) > config.maxAbsoluteInput || Math.abs(value) > config.maxAbsoluteInteger) {
            errors.push({ field, code: 'OUT_OF_RANGE', message: `${field} izin verilen aralığın dışındadır.` });
        }
    }
    if (!['add', 'subtract', 'multiply', 'divide'].includes(input.operation)) {
        errors.push({ field: 'operation', code: 'UNSUPPORTED_VALUE', message: 'Kesir işlemi desteklenmiyor.' });
    }
    if (input.denominator1 === 0)
        errors.push({ field: 'denominator1', code: 'DIVISION_BY_ZERO', message: 'İlk payda sıfır olamaz.' });
    if (input.denominator2 === 0)
        errors.push({ field: 'denominator2', code: 'DIVISION_BY_ZERO', message: 'İkinci payda sıfır olamaz.' });
    if (input.operation === 'divide' && input.numerator2 === 0) {
        errors.push({ field: 'numerator2', code: 'DIVISION_BY_ZERO', message: 'Sıfır olan ikinci kesire bölme yapılamaz.' });
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const p1 = BigInt(input.numerator1);
    const q1 = BigInt(input.denominator1);
    const p2 = BigInt(input.numerator2);
    const q2 = BigInt(input.denominator2);
    let numerator;
    let denominator;
    switch (input.operation) {
        case 'add':
            numerator = p1 * q2 + p2 * q1;
            denominator = q1 * q2;
            break;
        case 'subtract':
            numerator = p1 * q2 - p2 * q1;
            denominator = q1 * q2;
            break;
        case 'multiply':
            numerator = p1 * p2;
            denominator = q1 * q2;
            break;
        case 'divide':
            numerator = p1 * q2;
            denominator = q1 * p2;
            break;
    }
    [numerator, denominator] = simplifyFraction(numerator, denominator);
    const decimal = Number(numerator) / Number(denominator);
    if (!Number.isFinite(decimal)) {
        return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Ondalık sonuç sayı aralığını aştı.' }], metadata);
    }
    const absoluteNumerator = bigAbs(numerator);
    const whole = absoluteNumerator / denominator;
    const remainder = absoluteNumerator % denominator;
    const mixed = whole === 0n || remainder === 0n
        ? null
        : { whole: `${numerator < 0n ? '-' : ''}${whole}`, numerator: remainder.toString(), denominator: denominator.toString() };
    return success({
        operation: input.operation,
        numerator: numerator.toString(),
        denominator: denominator.toString(),
        fraction: denominator === 1n ? numerator.toString() : `${numerator}/${denominator}`,
        decimal,
        mixed,
    }, metadata);
}
